import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setErrorMsg('訊息內容不得超過500字');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6i5val4ieqv1pmm5ge0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
      } else {
        setStatus('error');
        setErrorMsg('提交失敗，請稍後再試。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('網路錯誤，請稍後再試。');
    }
  };

  const contactInfo = [
    { icon: 'ri-map-pin-2-line', label: '公司地址', value: '桃園市中壢區長安街78號', sub: '' },
    { icon: 'ri-mail-line', label: '電子信箱', value: 'enchengai.com@gmail.com', sub: '48小時內回覆' },
    { icon: 'ri-customer-service-2-line', label: '線上客服', value: 'LINE ID: encheng_ai', sub: '即時回覆' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20">
        <div className="absolute inset-0 z-0 h-72">
          <img
            src="https://public.readdy.ai/ai/img_res/595a411b845b3fb80330b98edf81ad9f.jpg"
            alt="Contact Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-8 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-md rounded-full text-teal-300 mb-6 border border-teal-400/30">
            <i className="ri-mail-send-line"></i>
            <span className="text-sm font-medium">聯絡我們</span>
          </div>
          <h1 className="text-5xl font-extrabold text-white mb-5 leading-tight">
            開始您的數位轉型之旅
          </h1>
          <p className="text-white/80 text-lg max-w-xl mx-auto leading-relaxed">
            填寫以下表單，我們的專家將在 48 小時內與您聯繫，為您提供免費的企業數位化診斷。
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* Left: Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <AnimatedSection animation="fadeRight">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">與我們聯繫</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                  無論您有任何問題或需求，我們的專業團隊隨時準備為您提供協助。
                </p>
                <div className="space-y-4">
                  {contactInfo.map((info) => (
                    <div key={info.label} className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 hover:shadow-md hover:shadow-teal-500/10 transition-all duration-300">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex-shrink-0 shadow-lg shadow-teal-500/20">
                        <i className={`${info.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-0.5">{info.label}</p>
                        <p className="text-gray-900 font-semibold text-sm">{info.value}</p>
                        {info.sub && <p className="text-xs text-teal-600 mt-0.5">{info.sub}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeRight" delay={200}>
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-6 border border-teal-100">
                <h3 className="font-bold text-gray-900 mb-3 text-base flex items-center gap-2">
                  <i className="ri-time-line text-teal-500"></i>
                  服務時間
                </h3>
                <div className="space-y-2 text-sm">
                  {[
                    { day: '週一 ～ 週五', time: '09:00 – 18:00' },
                    { day: '週六', time: '10:00 – 15:00（預約制）' },
                    { day: '週日及國定假日', time: '休息' },
                  ].map((item) => (
                    <div key={item.day} className="flex justify-between items-center py-1.5 border-b border-teal-100 last:border-0">
                      <span className="text-gray-600">{item.day}</span>
                      <span className="font-medium text-teal-700">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <AnimatedSection animation="fadeLeft">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-xl shadow-gray-100/50 p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">填寫諮詢表單</h2>

                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal-50 border-2 border-teal-200 mb-5">
                      <i className="ri-checkbox-circle-line text-teal-500 text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">感謝您的諮詢！</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
                      我們已收到您的訊息，專業顧問將在 48 小時內與您聯繫，請留意您的電子信箱。
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-6 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium text-sm cursor-pointer hover:scale-105 transition-all whitespace-nowrap"
                    >
                      再次填寫
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          姓名 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="請輸入您的姓名"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          電子信箱 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">公司名稱</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="請輸入公司名稱"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">聯絡電話</label>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="請輸入聯絡電話"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">感興趣的服務</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all bg-white cursor-pointer"
                      >
                        <option value="">請選擇服務項目</option>
                        <option value="數據分析與預測">數據分析與預測</option>
                        <option value="客製化微型工具">客製化微型工具</option>
                        <option value="AI自動化工具設計">AI自動化工具設計</option>
                        <option value="數位轉型顧問">數位轉型顧問</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        需求說明 <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="請簡述您的企業現況與需求，我們將為您提供最適合的解決方案..."
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all resize-none"
                      />
                      <div className="flex justify-between mt-1">
                        <span className={`text-xs ${formData.message.length > 500 ? 'text-red-400' : 'text-gray-400'}`}>
                          {formData.message.length}/500 字
                        </span>
                      </div>
                    </div>

                    {errorMsg && (
                      <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                        <i className="ri-error-warning-line text-base"></i>
                        <span>{errorMsg}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading' || formData.message.length > 500}
                      className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-semibold text-sm cursor-pointer shadow-lg shadow-teal-500/30 hover:from-teal-600 hover:to-emerald-600 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {status === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-lg"></i>
                          提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-lg"></i>
                          送出諮詢表單
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      提交即表示您同意我們的隱私政策，您的資料將受到嚴格保護。
                    </p>
                  </form>
                )}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-8">
          <AnimatedSection animation="fadeUp" className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">公司位置</h2>
            <p className="text-gray-500 text-sm">桃園市中壢區長安街78號</p>
          </AnimatedSection>
          <AnimatedSection animation="scale">
            <div className="w-full h-80 rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3614.8!2d121.5654!3d25.0330!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442abb6da9c9e2f%3A0x1234567890abcdef!2z5Y-w5YyX5biC5L-h5YaF5Y2A5p2x5LqL6Lev100!5e0!3m2!1szh-TW!2stw!4v1700000000000!5m2!1szh-TW!2stw"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="恩誠國際股份有限公司位置"
              ></iframe>
            </div>
          </AnimatedSection>
        </div>
      </div>

      <Footer />
    </div>
  );
}
